import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

// Mirrors the backend Notification payload (cgt-backend NotificationsController).
// The FE surface is titled "Notifications"; severity mirrors the alert type.
export interface Notification {
  id: string
  type: 'INFO' | 'WARNING' | 'ERROR'
  message: string
  category: string
  subjectRef?: string | null
  readAt: string | null
  createdAt: string
  payload?: unknown
}

// The categories the backend currently emits. `Notification.category` above stays
// a plain string on purpose: unknown categories must stay renderable (see the
// tolerant `default:` arm in @/lib/notificationLink).
export type NotificationCategory =
  | 'PATIENT_INTAKE'
  | 'LAB_RESULT'
  | 'PROCEDURE_DUE'
  | 'FORM_NOTIFICATION_RULE'
  | 'LIS_RESULT_REVIEW'
  // Reserved (z8nrz7fh5c): an offline mobile action refused or in conflict on
  // sync. Nothing sends it yet.
  | 'OFFLINE_MOBILE_ACTION'
  // Title21 Support answered or moved a ticket this tenant raised (src/support).
  | 'SUPPORT_TICKET'

export interface UnreadCounts {
  count: number
  byCategory: Partial<Record<NotificationCategory, number>>
}

export interface NotificationPage {
  items: Notification[]
  nextCursor: string | null
}

export interface ListNotificationsParams {
  unreadOnly?: boolean
  /** Only these categories. Absent or empty: every category. */
  categories?: readonly NotificationCategory[]
  cursor?: string
  limit?: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

function buildQuery(params: ListNotificationsParams): string {
  const qs = new URLSearchParams()
  if (params.unreadOnly) qs.set('unreadOnly', 'true')
  if (params.categories?.length) qs.set('category', params.categories.join(','))
  if (params.cursor) qs.set('cursor', params.cursor)
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const notificationsApi = {
  list: (params: ListNotificationsParams = {}) =>
    request<NotificationPage>(`/v1/notifications${buildQuery(params)}`),

  unreadCount: () => request<UnreadCounts>('/v1/notifications/unread-count'),

  markRead: (id: string) =>
    request<Notification>(`/v1/notifications/${id}/read`, { method: 'POST' }),

  /** Marks every unread notification read, or only those in `categories`. */
  markAllRead: (categories?: readonly NotificationCategory[]) =>
    request<{ count: number }>(
      `/v1/notifications/read-all${categories?.length ? `?category=${categories.join(',')}` : ''}`,
      { method: 'POST' },
    ),
}
